/**
 * Provider picker - a searchable list, no map.
 *
 * Replaces the Google Maps modal. A map answers "where do I go", which is the
 * question REI's store picker exists for; here the customer goes nowhere and the
 * order ships to them either way, so the only thing the map had to add was
 * geography as a proxy for "which clinic is plausibly mine" - and a list sorted
 * by distance says that better. With ~1,560 providers the map could not say it
 * at all: every pin rendered at once covered the country in a solid block, and
 * the 14.6% of them that cannot be selected looked exactly like the rest.
 *
 * Searching runs against the full list in memory rather than the API, because
 * /providers only matches a query parameter exactly - ?name=Duo returns nothing
 * while ?name=Duo%20Wellness returns the record - so a customer typing a name
 * could never be served by it.
 */
(function () {
  if (window.__providerPicker) return;
  window.__providerPicker = true;

  const LOCATION_KEY = "epicutis:auto-assign:location";
  const PAGE_SIZE = 25;
  const SEARCH_DEBOUNCE_MS = 180;
  const KM_PER_MILE = 1.609344;

  const ids = {
    dialog: "ProviderPicker",
    query: "ProviderPickerQuery",
    status: "ProviderPickerStatus",
    list: "ProviderPickerList",
    more: "ProviderPickerMore",
  };

  let dialog = null;
  let queryInput = null;
  let statusEl = null;
  let listEl = null;
  let moreBtn = null;

  let allProviders = [];
  let visible = [];
  let shown = 0;
  let origin = null;
  let searchOrigin = null;
  let loaded = false;

  // --- helpers ---------------------------------------------------------------

  function cachedOrigin() {
    try {
      const raw = window.sessionStorage.getItem(LOCATION_KEY);
      const parsed = raw && JSON.parse(raw);
      return (parsed && parsed.point) || null;
    } catch (error) {
      return null;
    }
  }

  function milesBetween(lng1, lat1, lng2, lat2) {
    const R = 3958.7613;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  // Distances are measured from whatever place the list is currently about:
  // the one searched for, or failing that the customer's own.
  function activeOrigin() {
    return searchOrigin || origin;
  }

  function withDistance(provider) {
    const from = activeOrigin();
    const coords = provider.location && provider.location.coordinates;
    if (!from || !Array.isArray(coords) || coords.length < 2) return null;
    // GeoJSON order is [lng, lat], not [lat, lng].
    return milesBetween(from.lng, from.lat, coords[0], coords[1]);
  }

  // A ZIP or a "City, ST" is a place, not a string to match against. Searching
  // one should answer "who is nearest to here", which is what the customer
  // means - a ZIP next door to a clinic's own, 97208 against 97209, otherwise
  // finds nothing at all.
  const geocodeCache = {};

  async function geocodePlace(query) {
    const text = query.trim();
    if (Object.prototype.hasOwnProperty.call(geocodeCache, text)) return geocodeCache[text];

    let url = null;
    const zip = text.match(/^(\d{5})(?:-\d{4})?$/);
    const cityState = text.match(/^([A-Za-z .'-]+?)[,\s]+([A-Za-z]{2})$/);
    if (zip) {
      url = `https://api.zippopotam.us/us/${zip[1]}`;
    } else if (cityState) {
      url = `https://api.zippopotam.us/us/${cityState[2].toLowerCase()}/${encodeURIComponent(cityState[1].trim())}`;
    }
    if (!url) return (geocodeCache[text] = null);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      const place = data.places && data.places[0];
      if (!place) throw new Error("empty");
      const lng = parseFloat(place.longitude);
      const lat = parseFloat(place.latitude);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) throw new Error("bad coords");
      const label = zip
        ? `${place["place name"]}, ${place["state abbreviation"]} ${zip[1]}`
        : `${data["place name"] || place["place name"]}, ${(cityState[2] || "").toUpperCase()}`;
      return (geocodeCache[text] = { point: { lng, lat }, label });
    } catch (error) {
      return (geocodeCache[text] = null);
    }
  }

  // Measured in miles throughout so the sort and the thresholds stay in one
  // unit; AU and NZ change only what the row reads. isMetricMarket comes from
  // provider-locator-map.js, which the section loads ahead of this file.
  function distanceLabel(miles) {
    if (miles === null) return "";
    if (typeof isMetricMarket === "function" && isMetricMarket()) {
      const km = miles * KM_PER_MILE;
      return km < 1 ? "< 1 km" : `${Math.round(km)} km`;
    }
    if (miles < 1) return "< 1 mile";
    return `${Math.round(miles)} miles`;
  }

  function addressLine(provider) {
    if (provider.address) return provider.address;
    return [provider.streetAddress1, provider.city, provider.state, provider.zipCode]
      .filter(Boolean)
      .join(", ");
  }

  function isAccruing(provider) {
    return typeof window.isAccruingProvider === "function"
      ? window.isAccruingProvider(provider)
      : provider.status === "Active";
  }

  function currentCode() {
    const root = document.querySelector("[data-provider-pdp]");
    if (root && root.dataset.code) return root.dataset.code;
    const state = window.ProviderStateManager && window.ProviderStateManager.getState();
    return (state && state.provider && state.provider.code) || null;
  }

  // --- data ------------------------------------------------------------------

  async function load() {
    if (loaded) return;
    // getProviders() caches for 5 minutes in provider-locator-map.js.
    const providers = typeof window.getProviders === "function" ? await window.getProviders() : [];
    allProviders = (Array.isArray(providers) ? providers : []).filter(
      // Draft records are not published providers; they should not be offered.
      (p) => p && p.status !== "Draft"
    );
    loaded = true;
  }

  function sortByDistance(list) {
    // Same origin the rows measure from. Guarding on the customer's own
    // location instead left a searched ZIP showing distances while the list
    // kept the order the API sent it in.
    if (!activeOrigin()) return list;
    return list
      .map((p) => ({ p, d: withDistance(p) }))
      .sort((a, b) => (a.d === null ? Infinity : a.d) - (b.d === null ? Infinity : b.d))
      .map((x) => x.p);
  }

  function matches(provider, terms) {
    const haystack = [provider.name, provider.city, provider.state, provider.zipCode, provider.address]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return terms.every((term) => haystack.includes(term));
  }

  // Waiting with no sign of life reads as a finished answer - an empty
  // list, or a search that found nothing. The dots say it is still working.
  function busy(message) {
    statusEl.textContent = message + "\u00a0";
    const dots = document.createElement("span");
    dots.className = "pp__dots";
    dots.setAttribute("aria-hidden", "true");
    for (let i = 0; i < 3; i += 1) dots.appendChild(document.createElement("i"));
    statusEl.appendChild(dots);
  }

  let querySeq = 0;

  async function applyQuery(raw) {
    const seq = ++querySeq;
    const text = String(raw || "").trim();

    // The list takes a few seconds to arrive. Typing before it does must not
    // report an empty result - open() re-runs this with the current input once
    // the load finishes.
    if (!loaded) {
      busy("Loading providers");
      return;
    }

    // A ZIP or a city has to reach the geocoder before a row can be drawn.
    if (text) busy("Searching");
    const place = text ? await geocodePlace(text) : null;
    // A lookup that resolves after the customer has typed on must not redraw
    // the list underneath what they are looking at now.
    if (seq !== querySeq) return;
    searchOrigin = place ? place.point : null;

    const terms = place
      ? []
      : text
          .toLowerCase()
          .split(/[\s,]+/)
          .filter(Boolean);

    visible = sortByDistance(terms.length ? allProviders.filter((p) => matches(p, terms)) : allProviders);
    shown = 0;
    listEl.innerHTML = "";
    renderMore();
    announce(text, terms.length, place);
  }

  function announce(raw, filtered, place) {
    if (!visible.length) {
      statusEl.textContent = filtered
        ? `No providers match "${raw}". Try a ZIP code, a city, or part of the name.`
        : "No providers available right now.";
      return;
    }
    if (place) {
      statusEl.textContent = `Providers nearest to ${place.label}.`;
      return;
    }
    const where = activeOrigin() ? " Nearest first." : "";
    statusEl.textContent = filtered
      ? `${visible.length} ${visible.length === 1 ? "provider" : "providers"} found.${where}`
      : `${visible.length} providers.${where}`;
  }

  // --- rendering -------------------------------------------------------------

  function row(provider) {
    const li = document.createElement("li");
    li.className = "pp__item";
    if (provider.providerCode === currentCode()) li.classList.add("is-current");

    const main = document.createElement("div");
    main.className = "pp__item-main";

    const name = document.createElement("p");
    name.className = "pp__item-name font-medium leading-tight";
    name.textContent = provider.name || provider.providerCode;
    main.appendChild(name);

    const addr = document.createElement("p");
    addr.className = "pp__item-addr text-sm leading-tight";
    addr.textContent = addressLine(provider);
    main.appendChild(addr);
    li.appendChild(main);

    const side = document.createElement("div");
    side.className = "pp__item-side";

    const miles = withDistance(provider);
    if (miles !== null) {
      const dist = document.createElement("span");
      dist.className = "pp__item-dist text-sm leading-tight";
      dist.textContent = distanceLabel(miles);
      side.appendChild(dist);
    }

    if (!isAccruing(provider)) {
      // Non-accruing providers earn the clinic nothing on an online order, so
      // they are listed but cannot be chosen - same rule the old map used.
      const note = document.createElement("p");
      note.className = "pp__item-note text-xs leading-tight";
      note.textContent = "Available for in store purchase only.";
      side.appendChild(note);
    } else if (provider.providerCode === currentCode()) {
      const tag = document.createElement("span");
      tag.className = "pp__item-current text-sm leading-tight";
      tag.textContent = "Selected";
      side.appendChild(tag);
    } else {
      // The theme's hover turns the label the colour of the background and
      // relies on the .btn-fill circle sliding in behind it, so a bare .button
      // ends up black on black. Build what it expects.
      const button = document.createElement("button", { is: "hover-button" });
      button.type = "button";
      button.className = "pp__select button button--primary button--small";

      const fill = document.createElement("span");
      fill.className = "btn-fill";
      fill.setAttribute("data-fill", "");
      button.appendChild(fill);

      const label = document.createElement("span");
      label.className = "btn-text";
      label.textContent = "Select";
      button.appendChild(label);

      button.addEventListener("click", () => choose(provider));
      side.appendChild(button);
    }

    li.appendChild(side);
    return li;
  }

  function renderMore() {
    const slice = visible.slice(shown, shown + PAGE_SIZE);
    const frag = document.createDocumentFragment();
    slice.forEach((p) => frag.appendChild(row(p)));
    listEl.appendChild(frag);
    shown += slice.length;
    moreBtn.hidden = shown >= visible.length;
    moreBtn.textContent = `Show ${Math.min(PAGE_SIZE, visible.length - shown)} more`;
  }

  async function choose(provider) {
    if (typeof window.addToCartAttribute !== "function") return;
    await window.addToCartAttribute(provider.providerCode, provider, "manual");
    close();
  }

  // --- open / close ----------------------------------------------------------

  async function open() {
    if (!dialog) return;
    origin = cachedOrigin();
    searchOrigin = null;
    if (!dialog.open) dialog.showModal();
    busy("Loading providers");
    try {
      await load();
      applyQuery(queryInput.value);
    } catch (error) {
      statusEl.textContent = "Could not load providers. Please try again.";
    }
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
  }

  // --- wiring ----------------------------------------------------------------

  function init() {
    dialog = document.getElementById(ids.dialog);
    if (!dialog) return;
    queryInput = document.getElementById(ids.query);
    statusEl = document.getElementById(ids.status);
    listEl = document.getElementById(ids.list);
    moreBtn = document.getElementById(ids.more);

    let timer = null;
    queryInput.addEventListener("input", () => {
      clearTimeout(timer);
      // The debounce plus the geocode round trip is long enough to look
      // stuck. Say so now, not when applyQuery finally gets to run.
      if (queryInput.value.trim()) busy("Searching");
      timer = setTimeout(() => applyQuery(queryInput.value), SEARCH_DEBOUNCE_MS);
    });

    moreBtn.addEventListener("click", renderMore);
    dialog.querySelectorAll("[data-provider-picker-close]").forEach((el) => el.addEventListener("click", close));

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-open-provider-modal]")) {
        event.preventDefault();
        open();
      }
    });

    // checkout-validation.js and provider-state-manager.js reach for this; both
    // guard it, but keeping the shape means they take their normal path.
    const instance = { open, close, get dialog() { return dialog; } };
    window.ProviderModal = { getInstance: () => instance };
    window.ProviderPicker = instance;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
